import { diffLines, Change } from 'diff';

/**
 * Single source of truth for line diffing, chunk navigation and merge output.
 *
 * App.tsx and DiffVisualizer.tsx both consume the array returned by
 * computeLineDiff(), so chunk indices used for navigation, rendering and
 * merge choices always refer to the same change objects.
 */

export interface DiffOptions {
  /** Ignore leading/trailing whitespace when comparing lines. */
  ignoreWhitespace: boolean;
  /** Compare lines case-insensitively (display and merge keep original casing). */
  ignoreCase: boolean;
  /** Treat hunks made only of blank lines as non-changes. */
  ignoreBlankLines: boolean;
}

export interface DiffChange extends Change {
  /**
   * Original-side text for unchanged chunks. jsdiff reports the *modified*
   * side's text for lines that only match thanks to ignore options, so this
   * keeps the left pane and the merged output faithful to the original.
   */
  leftValue?: string;
  /** Added/removed hunk that only differs in blank lines while ignoreBlankLines is on. */
  ignored?: boolean;
}

export type ChunkKind = 'Added' | 'Removed' | 'Modified';

export interface DiffChunkMeta {
  /** Index into the DiffChange array (the removed half for Modified pairs). */
  index: number;
  type: ChunkKind;
  summary: string;
}

/** Merge choices keyed by DiffChange index; the value is the text to emit. */
export type MergeChoices = Record<number, string>;

export type MergeKeep = 'original' | 'modified';

/** Split into lines, keeping each line's terminator (matches jsdiff's line tokens). */
export function splitLinesKeepEol(text: string): string[] {
  return text.match(/[^\n]*\n|[^\n]+$/g) ?? [];
}

const isBlankOnly = (value: string): boolean => value.trim() === '';

const isPairAt = (changes: DiffChange[], i: number): boolean =>
  Boolean(changes[i]?.removed && changes[i + 1]?.added);

export function computeLineDiff(left: string, right: string, options: DiffOptions): DiffChange[] {
  // jsdiff's base comparator honours ignoreCase for every tokenizer, but the
  // typings only declare it for diffChars/diffWords; a non-literal object
  // avoids the excess-property check without resorting to `any`.
  const jsdiffOptions = { ignoreWhitespace: options.ignoreWhitespace, ignoreCase: options.ignoreCase };
  const raw = diffLines(left, right, jsdiffOptions);

  const leftLines = splitLinesKeepEol(left);
  let leftCursor = 0;
  const result: DiffChange[] = raw.map((change) => {
    const count = change.count ?? splitLinesKeepEol(change.value).length;
    if (change.added) return { ...change };
    if (change.removed) {
      leftCursor += count;
      return { ...change };
    }
    const leftValue = leftLines.slice(leftCursor, leftCursor + count).join('');
    leftCursor += count;
    return { ...change, leftValue };
  });

  if (options.ignoreBlankLines) {
    for (let i = 0; i < result.length; i++) {
      if (isPairAt(result, i)) {
        if (isBlankOnly(result[i].value) && isBlankOnly(result[i + 1].value)) {
          result[i].ignored = true;
          result[i + 1].ignored = true;
        }
        i++;
      } else if ((result[i].added || result[i].removed) && isBlankOnly(result[i].value)) {
        result[i].ignored = true;
      }
    }
  }

  return result;
}

export function summarizeChunks(changes: DiffChange[], snippetLength = 45): DiffChunkMeta[] {
  const chunks: DiffChunkMeta[] = [];
  for (let i = 0; i < changes.length; i++) {
    const change = changes[i];
    if (!change.added && !change.removed) continue;
    const pair = isPairAt(changes, i);
    if (!change.ignored) {
      const type: ChunkKind = pair ? 'Modified' : change.added ? 'Added' : 'Removed';
      const content = pair ? changes[i + 1].value : change.value;
      const flat = content.replace(/\s+/g, ' ').trim();
      const snippet = flat.length > snippetLength ? `${flat.slice(0, snippetLength)}...` : flat;
      chunks.push({ index: i, type, summary: `${type}: ${snippet}` });
    }
    if (pair) i++;
  }
  return chunks;
}

/** Record a keep-original / take-modified decision for the chunk containing `index`. */
export function resolveChunk(
  changes: DiffChange[],
  choices: MergeChoices,
  index: number,
  keep: MergeKeep,
): MergeChoices {
  const change = changes[index];
  if (!change || (!change.added && !change.removed)) return choices;

  const next = { ...choices };
  const pairStart = isPairAt(changes, index) ? index : isPairAt(changes, index - 1) ? index - 1 : -1;
  if (pairStart >= 0) {
    next[pairStart] = keep === 'original' ? changes[pairStart].value : '';
    next[pairStart + 1] = keep === 'modified' ? changes[pairStart + 1].value : '';
  } else if (change.removed) {
    next[index] = keep === 'original' ? change.value : '';
  } else {
    next[index] = keep === 'modified' ? change.value : '';
  }
  return next;
}

/**
 * Build merged output. Unresolved chunks default to the original text:
 * removed lines are kept, added lines are dropped.
 */
export function buildMergedText(changes: DiffChange[], choices: MergeChoices): string {
  let out = '';
  for (let i = 0; i < changes.length; i++) {
    const change = changes[i];
    if (isPairAt(changes, i)) {
      const resolved = choices[i] !== undefined || choices[i + 1] !== undefined;
      out += resolved ? (choices[i] ?? '') + (choices[i + 1] ?? '') : change.value;
      i++;
    } else if (change.removed) {
      out += choices[i] ?? change.value;
    } else if (change.added) {
      out += choices[i] ?? '';
    } else {
      out += change.leftValue ?? change.value;
    }
  }
  return out;
}

/** Changed line counts, excluding ignored hunks. */
export function countChangedLines(changes: DiffChange[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const change of changes) {
    if (change.ignored) continue;
    const lines = change.count ?? splitLinesKeepEol(change.value).length;
    if (change.added) added += lines;
    else if (change.removed) removed += lines;
  }
  return { added, removed };
}

/** Indices of chunks that have a merge decision (both halves of a pair are reported). */
export function resolvedIndices(choices: MergeChoices): Set<number> {
  return new Set(Object.keys(choices).map(Number));
}
